// src/pages/Auth/components/SignupForm.js

import React, { useState, useCallback } from 'react';
import { Box, Grid, Typography, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { LOADING_STATES } from '../constants';
import { StyledInput, StyledButton } from '../styles/Styles';
import { validatePassword } from '../utils';

const SignupForm = ({ email, isDarkMode, loadingState, onSubmit, showSnackbar }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState(null);

  const validatePasswordRules = useCallback((password) => {
    const validation = validatePassword(password);
    setPasswordValidation(validation.rules);
    return validation.isValid;
  }, []);

  const handlePasswordChange = useCallback((e) => {
    const newPassword = e.target.value;
    setFormData(prev => ({ ...prev, password: newPassword }));
    validatePasswordRules(newPassword);
  }, [validatePasswordRules]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      showSnackbar("Please fill in all password fields", "error");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      showSnackbar("Passwords do not match!", "error");
      return;
    }

    if (!validatePasswordRules(formData.password)) {
      showSnackbar("Password does not meet the required criteria", "error");
      return;
    }

    await onSubmit(formData);
  };

  const renderRule = (label, passed) => (
    <Box display="flex" alignItems="center" mb={1}> 
      {passed ? ( 
        <CheckCircleIcon sx={{ mr: 1, color: "#219297", fontSize: "16px" }} />
      ) : ( 
        <CancelIcon color="error" sx={{ mr: 1, fontSize: "16px" }} /> 
      )} 
      <Typography variant="body2" color={passed ? "#219297" : (isDarkMode ? "#a0aec0" : "text.secondary")} sx={{ fontSize: "13px" }}> 
        {label} 
      </Typography> 
    </Box>
  );

  const isLoading = loadingState === LOADING_STATES.SIGNUP;
  const allRulesPassed = passwordValidation ? Object.values(passwordValidation).every(Boolean) : false;

  return (
    <form onSubmit={handleSubmit}>
      <Box>
        <Box 
          sx={{ 
            mb: 3,
            p: 2,
            backgroundColor: isDarkMode ? "#0a1020" : "#f0fdfa",
            borderRadius: "10px",
            border: `1px solid ${isDarkMode ? "#2d2d3f" : "#ccfbf1"}`,
          }}
        >
          <Typography variant="body2" sx={{ color: isDarkMode ? "#a0aec0" : "text.secondary", fontSize: "14px", mb: 1 }}>
            Completing signup for
          </Typography>
          <Typography variant="body1" fontWeight="600" sx={{ color: "#219297" }}>
            {email}
          </Typography>
        </Box>
        
        <Grid container spacing={2} mb={3}>
          <Grid item xs={6}>
            <Box mb={2}>
              <Typography 
                component="label" 
                htmlFor="firstName"
                sx={{ 
                  fontSize: "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: isDarkMode ? "#e2e8f0" : "#374151"
                }}
              >
                First Name
              </Typography>
              <StyledInput
                id="firstName"
                type="text"
                value={formData.firstName}
                onChange={(e) => setFormData(prev => ({...prev, firstName: e.target.value}))}
                required
                placeholder="First Name"
                isDarkMode={isDarkMode}
                sx={{ padding: "12px 14px", fontSize: "14px" }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box mb={2}>
              <Typography 
                component="label" 
                htmlFor="lastName"
                sx={{ 
                  fontSize: "14px", 
                  fontWeight: "600", 
                  marginBottom: "8px", 
                  display: "block",
                  color: isDarkMode ? "#e2e8f0" : "#374151"
                }}
              >
                Last Name
              </Typography>
              <StyledInput
                id="lastName"
                type="text"
                value={formData.lastName}
                onChange={(e) => setFormData(prev => ({...prev, lastName: e.target.value}))}
                required
                placeholder="Last Name"
                isDarkMode={isDarkMode}
                sx={{ padding: "12px 14px", fontSize: "14px" }}
              />
            </Box>
          </Grid>
        </Grid>

        <Box mb={2} sx={{ position: "relative" }}>
          <Typography 
            component="label" 
            htmlFor="password"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Password
          </Typography>
          <StyledInput
            id="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handlePasswordChange}
            required
            placeholder="Enter password"
            isDarkMode={isDarkMode}
            sx={{ padding: "12px 40px 12px 14px", fontSize: "14px" }}
          />
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "34px",
              padding: "8px",
            }}
          >
            {showPassword ? 
              <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : 
              <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />
            }
          </IconButton>
        </Box>

        {formData.password && passwordValidation && (
          <Box mb={2} sx={{ mt: 1 }}>
            {renderRule("At least 8 characters", passwordValidation.minLength)}
            {renderRule("Includes a number", passwordValidation.number)}
            {renderRule("Includes a special character", passwordValidation.specialChar)}
            {renderRule("Includes Uppercase [A-Z]", passwordValidation.uppercase)}
            {renderRule("Includes Lowercase [a-z]", passwordValidation.lowercase)}
          </Box>
        )}

        <Box mb={3} sx={{ position: "relative" }}>
          <Typography 
            component="label" 
            htmlFor="confirmPassword"
            sx={{ 
              fontSize: "14px", 
              fontWeight: "600", 
              marginBottom: "8px", 
              display: "block",
              color: isDarkMode ? "#e2e8f0" : "#374151"
            }}
          >
            Confirm Password
          </Typography>
          <StyledInput
            id="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            value={formData.confirmPassword}
            onChange={(e) => setFormData(prev => ({...prev, confirmPassword: e.target.value}))}
            required
            placeholder="Confirm password"
            isDarkMode={isDarkMode}
            sx={{ padding: "12px 40px 12px 14px", fontSize: "14px" }}
          />
          <IconButton
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            sx={{
              position: "absolute",
              right: "8px",
              top: "34px",
              padding: "8px",
            }}
          >
            {showConfirmPassword ? 
              <VisibilityOff fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} /> : 
              <Visibility fontSize="small" sx={{ color: isDarkMode ? "#a0aec0" : undefined }} />
            }
          </IconButton>
        </Box>

        <StyledButton
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading || !allRulesPassed}
          isDarkMode={isDarkMode}
          sx={{
            backgroundColor: (!allRulesPassed) ? (isDarkMode ? "#2d2d3f" : "#ccc") : undefined,
          }}
        >
          {isLoading ? "Creating Account..." : "Complete Signup"}
        </StyledButton>
      </Box>
    </form>
  );
};

export default React.memo(SignupForm);